import * as React from 'react'
import {
    Card, CardContent, Typography, TextField, Button, Grid, Alert,
    TableRow, TableCell,
} from "@mui/material"
import { useFormik } from 'formik';
import { useState, useEffect, useRef } from 'react';
import * as yup from 'yup';
import { authenticatedFetch } from '../utils/api';

// Helper function to parse UTC dates
const parseUtcDate = (value) => {
    if (!value) return null

    // If server returns ISO with timezone (best case)
    if (typeof value === 'string' && (value.includes('Z') || /[+-]\d\d:\d\d$/.test(value))) {
        const d = new Date(value)
        return Number.isNaN(d.getTime()) ? null : d
    }

    // If server returns "YYYY-MM-DD HH:MM:SS" (no timezone), treat it as UTC
    if (typeof value === 'string' && value.includes(' ') && !value.includes('T')) {
        const utcIso = value.replace(' ', 'T') + 'Z'
        const d = new Date(utcIso)
        return Number.isNaN(d.getTime()) ? null : d
    }

    // Fallback: let the browser try
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? null : d
}

function Predictions({ fixture, existingPrediction, onPredictionSaved, asTableRow, allowAskAi = false, leagueId = null }) {
    const {fixture_round, fixture_date, fixture_home_team, fixture_away_team, id} = fixture
    const [message, setMessage] = useState(null)
    const [saving, setSaving] = useState(false)
    const [askingAi, setAskingAi] = useState(false)
    
    // Check if the game has started (lock predictions after kickoff time)
    const isGameStarted = () => {
        if (!fixture_date) return false
        const gameDate = parseUtcDate(fixture_date)
        if (!gameDate) return false
        return new Date() >= gameDate
    }
    
    const gameStarted = isGameStarted()

    const handleAskAi = () => {
        if (gameStarted) return
        setAskingAi(true)
        setMessage(null)
        authenticatedFetch('/api/v1/predictions/ask-ai', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ fixture_id: id, league_id: leagueId }),
        })
            .then((response) => {
                if (!response.ok) {
                    return response.json().then((err) => {
                        throw new Error(err.error || 'Failed to get AI prediction')
                    })
                }
                return response.json()
            })
            .then((data) => {
                formik.setFieldValue('home_team_score', data.home_team_score)
                formik.setFieldValue('away_team_score', data.away_team_score)
                formik.setFieldTouched('home_team_score', true)
                formik.setFieldTouched('away_team_score', true)
                if (data.rationale) {
                    setMessage({ type: 'info', text: data.rationale })
                    setTimeout(() => setMessage(null), 8000)
                } else {
                    setMessage({ type: 'success', text: 'AI suggestion applied. Edit if you like and save.' })
                    setTimeout(() => setMessage(null), 4000)
                }
            })
            .catch((error) => {
                setMessage({ type: 'error', text: error.message || 'Could not get AI prediction.' })
            })
            .finally(() => setAskingAi(false))
    }

    const predictionSchema = yup.object().shape({
        home_team_score: yup.number()
            .integer('Score must be a whole number')
            .min(0, 'Score cannot be negative')
            .required('Home team score is required'),
        away_team_score: yup.number()
            .integer('Score must be a whole number')
            .min(0, 'Score cannot be negative')
            .required('Away team score is required')
    })

    const formik = useFormik({
        initialValues: {
            home_team_score: existingPrediction?.home_team_score ?? '',
            away_team_score: existingPrediction?.away_team_score ?? '',
        },
        enableReinitialize: true,
        validationSchema: predictionSchema,
        onSubmit: (values) => {
            // Never send if game has started (defense in depth; inputs are already disabled)
            if (gameStarted) return
            setSaving(true)
            setMessage(null)
            
            authenticatedFetch('/api/v1/predictions', {
                method: 'POST',
                body: JSON.stringify({
                    fixture_id: id,
                    league_id: leagueId,
                    home_team_score: parseInt(values.home_team_score),
                    away_team_score: parseInt(values.away_team_score)
                })
            })
            .then(response => {
                if (!response.ok) {
                    return response.json().then(err => {
                        throw new Error(err.error || 'Failed to save prediction')
                    })
                }
                return response.json()
            })
            .then(data => {
                setMessage({ type: 'success', text: 'Prediction saved successfully!' })
                setTimeout(() => setMessage(null), 3000)
                // Refresh results if callback provided
                if (onPredictionSaved) {
                    onPredictionSaved()
                }
            })
            .catch(error => {
                console.error('Error saving prediction:', error)
                setMessage({ type: 'error', text: error.message || 'Failed to save prediction. Please try again.' })
            })
            .finally(() => {
                setSaving(false)
            })
        }
    })

    // Update form values when existingPrediction changes
    useEffect(() => {
        if (existingPrediction) {
            formik.setFieldValue('home_team_score', existingPrediction.home_team_score ?? '')
            formik.setFieldValue('away_team_score', existingPrediction.away_team_score ?? '')
        } else {
            // Clear form if no existing prediction
            formik.setFieldValue('home_team_score', '')
            formik.setFieldValue('away_team_score', '')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingPrediction?.id, existingPrediction?.home_team_score, existingPrediction?.away_team_score])

    // Auto-save when both scores are valid (debounced). Only when user has changed something.
    const autoSaveTimeoutRef = useRef(null)
    const isValidScore = (v) => v !== '' && Number.isInteger(Number(v)) && Number(v) >= 0
    useEffect(() => {
        if (gameStarted) return
        const home = formik.values.home_team_score
        const away = formik.values.away_team_score
        const bothValid = isValidScore(home) && isValidScore(away)
        if (!formik.dirty || !bothValid) return
        autoSaveTimeoutRef.current = setTimeout(() => {
            formik.submitForm()
        }, 700)
        return () => {
            if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.home_team_score, formik.values.away_team_score, formik.dirty, gameStarted])

    const formatKickoffLocal = (value) => {
        const date = parseUtcDate(value)
        if (!date) return 'Date TBD'
        return new Intl.DateTimeFormat(undefined, {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short',
        }).format(date)
    }

    // Day/Time for table: include date so you can compare to real-life league schedule (e.g. "Sat, Feb 23, 2026 · 10:00 AM")
    const formatDayTime = (value) => {
        const date = parseUtcDate(value)
        if (!date) return '—'
        const datePart = new Intl.DateTimeFormat(undefined, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        }).format(date)
        const timePart = new Intl.DateTimeFormat(undefined, {
            hour: 'numeric',
            minute: '2-digit',
        }).format(date)
        return `${datePart} · ${timePart}`
    }

    // Table row layout: Home | Score | Away | Score | Day/Time | Status (auto-save when both scores valid)
    const hasExisting = !!existingPrediction
    const saveButtonLabel = saving ? 'Saving...' : gameStarted ? (asTableRow ? 'Locked' : 'Game Started') : (hasExisting ? 'Update' : (asTableRow ? 'Save' : 'Save Prediction'))
    const saveButtonSx = hasExisting ? { backgroundColor: 'white', color: 'primary.main', borderColor: 'primary.main', '&:hover': { backgroundColor: 'grey.50', borderColor: 'primary.dark', color: 'primary.dark' } } : {}
    const rowStatus = saving ? 'Saving...' : message?.type === 'success' ? 'Saved' : ''
    if (asTableRow) {
        return (
            <TableRow>
                <TableCell sx={{ fontWeight: 500 }}>{fixture_home_team}</TableCell>
                <TableCell align="center" sx={{ width: 90 }}>
                    <TextField
                        size="small"
                        id={`home_team_score_${id}`}
                        name="home_team_score"
                        type="number"
                        variant="outlined"
                        value={formik.values.home_team_score}
                        onChange={formik.handleChange}
                        error={formik.touched.home_team_score && Boolean(formik.errors.home_team_score)}
                        inputProps={{ min: 0, max: 20, style: { textAlign: 'center' } }}
                        disabled={gameStarted}
                        sx={{ width: 64 }}
                    />
                </TableCell>
                <TableCell sx={{ fontWeight: 500 }}>{fixture_away_team}</TableCell>
                <TableCell align="center" sx={{ width: 90 }}>
                    <TextField
                        size="small"
                        id={`away_team_score_${id}`}
                        name="away_team_score"
                        type="number"
                        variant="outlined"
                        value={formik.values.away_team_score}
                        onChange={formik.handleChange}
                        error={formik.touched.away_team_score && Boolean(formik.errors.away_team_score)}
                        inputProps={{ min: 0, max: 20, style: { textAlign: 'center' } }}
                        disabled={gameStarted}
                        sx={{ width: 64 }}
                    />
                </TableCell>
                <TableCell align="center" sx={{ width: 80, fontSize: '0.875rem', color: 'text.secondary' }}>
                    {fixture.actual_home_score != null && fixture.actual_away_score != null
                        ? `${fixture.actual_home_score}–${fixture.actual_away_score}`
                        : '–'}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDayTime(fixture_date)}</TableCell>
                <TableCell align="center" sx={{ width: 140, fontSize: '0.875rem', color: 'text.secondary' }}>
                    {!gameStarted && allowAskAi && (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={handleAskAi}
                            disabled={askingAi}
                            sx={{ mb: 0.5 }}
                        >
                            {askingAi ? '…' : 'Ask AI'}
                        </Button>
                    )}
                    <div>{askingAi ? 'Getting suggestion…' : rowStatus}</div>
                    {message && (
                        <Typography variant="caption" display="block" sx={{ mt: 0.5, maxWidth: 200, textAlign: 'left' }}>
                            {message.text}
                        </Typography>
                    )}
                </TableCell>
            </TableRow>
        )
    }

    // Card layout (legacy / single fixture)
    return (
        <Card sx={{ mb: 2 }}>
            <CardContent>
                {message && (
                    <Alert severity={message.type} sx={{ mb: 2 }}>
                        {message.text}
                    </Alert>
                )}
                {gameStarted && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        This game has started. Predictions are locked and cannot be changed.
                    </Alert>
                )}
                <form onSubmit={formik.handleSubmit}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                {fixture_home_team}
                            </Typography>
                            <TextField
                                fullWidth
                                id="home_team_score"
                                name="home_team_score"
                                label="Home Score"
                                type="number"
                                variant="outlined"
                                value={formik.values.home_team_score}
                                onChange={formik.handleChange}
                                error={formik.touched.home_team_score && Boolean(formik.errors.home_team_score)}
                                helperText={formik.touched.home_team_score && formik.errors.home_team_score}
                                inputProps={{ min: 0, max: 20 }}
                                disabled={gameStarted}
                            />
                        </Grid>
                        <Grid item xs={12} md={1} sx={{ textAlign: 'center' }}>
                            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>vs</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                {fixture_away_team}
                            </Typography>
                            <TextField
                                fullWidth
                                id="away_team_score"
                                name="away_team_score"
                                label="Away Score"
                                type="number"
                                variant="outlined"
                                value={formik.values.away_team_score}
                                onChange={formik.handleChange}
                                error={formik.touched.away_team_score && Boolean(formik.errors.away_team_score)}
                                helperText={formik.touched.away_team_score && formik.errors.away_team_score}
                                inputProps={{ min: 0, max: 20 }}
                                disabled={gameStarted}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Button
                                fullWidth
                                type="submit"
                                variant={hasExisting ? 'outlined' : 'contained'}
                                color="primary"
                                disabled={saving || gameStarted}
                                sx={{ mt: { xs: 0, md: 4 }, ...saveButtonSx }}
                            >
                                {saveButtonLabel}
                            </Button>
                            {!gameStarted && allowAskAi && (
                                <Button
                                    fullWidth
                                    type="button"
                                    variant="outlined"
                                    color="secondary"
                                    onClick={handleAskAi}
                                    disabled={askingAi}
                                    sx={{ mt: 1 }}
                                >
                                    {askingAi ? 'Getting AI suggestion…' : 'Ask AI'}
                                </Button>
                            )}
                        </Grid>
                    </Grid>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                        {formatKickoffLocal(fixture_date)}
                    </Typography>
                </form>
            </CardContent>
        </Card>
    )
}

export default Predictions;